import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  getAuth,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import { support_phone_number, whatsAppUrl } from 'core/slices/constants';

import LoginLayout from './layout/LoginLayout';
import { getSafeErrorIdentifier } from '../utils/getSafeErrorIdentifier';
import { AppText, Button, InputField, toast } from '@zippeditoolsjs/ui-web';
import BackLink from '../ui/controls/BackLink';

// Deliberately loose: the authoritative check is the provider's, this only
// filters input that could never receive a mail.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Recover(props) {
  const { t } = useTranslation();
  // Set once the revocation succeeded, so the one-shot button stays dead even
  // after `revoking` clears. See revokeMFA.
  const [revoked, setRevoked] = useState(false);
  // `revoked` only arms two network hops in, so it cannot cover the in-flight
  // window: a second click would spend the same action code.
  const [revoking, setRevoking] = useState(false);
  const [language, setLanguage] = useState('en');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);
  const [email, setMail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const showError = (translationKey, variables) =>
    toast.error(t(translationKey, variables));
  const showSuccess = (translationKey, variables) =>
    toast.success(t(translationKey, variables));

  let search = useLocation();
  const navigate = useNavigate();
  let query = new URLSearchParams(search.search);
  let mode = query.get('mode');
  let tenant = query.get('tenantId');
  let code = query.get('oobCode');

  const handleClickShowPassword = () => setShowPassword(show => !show);

  const handleClickShowPasswordConfirmation = () =>
    setShowPasswordConfirmation(show => !show);

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  const handleMouseDownPasswordConfirmation = event => {
    event.preventDefault();
  };

  const handleChangeEmail = event => {
    setMail(event.target.value);
  };
  const handleChangePassword = event => {
    setPassword(event.target.value);
  };
  const handleChangePasswordConfirmation = event => {
    setPasswordConfirmation(event.target.value);
  };

  const fullWhatsAppUrl = `${whatsAppUrl}${support_phone_number}?text=${t(
    'heimdal_app.Login.help_initial_text',
  )}`;

  useEffect(() => {
    let languages = ['en', 'es', 'de'];
    let userLanguage = navigator.language || navigator.userLanguage || 'en-US';
    userLanguage = userLanguage.split('-')[0];
    userLanguage = languages.includes(userLanguage) ? userLanguage : 'en';

    setLanguage(userLanguage);
  }, []);

  useEffect(() => {
    if (props.hasValidatePasswordResponse) {
      const { message, isValidPassword } = props.hasValidatePasswordResponse;
      if (!isValidPassword) {
        showError('heimdal_app.PassRecovery.' + message);
      } else {
        const auth = getAuth();
        auth.tenantId = tenant;
        confirmPasswordReset(auth, code, password)
          .then(() => {
            showSuccess('heimdal_app.PassRecovery.password_changed');
          })
          .catch(error => {
            console.error(
              'Recover.confirmPasswordReset:',
              getSafeErrorIdentifier(error),
            );
            if (error.code === 'auth/weak-password') {
              showError('heimdal_app.PassRecovery.weak_password');
            } else {
              showError('heimdal_app.Login.try_again_later');
            }
          });
      }
    }
  }, [props.hasValidatePasswordResponse]);

  const revokeMFA = () => {
    const auth = getAuth();
    auth.tenantId = tenant;
    setRevoking(true);
    var obfuscatedPhoneNumber;
    checkActionCode(auth, code)
      .then(function (info) {
        var multiFactorInfo = info['data']['multiFactorInfo'];
        obfuscatedPhoneNumber = multiFactorInfo['phoneNumber'];
        return applyActionCode(auth, code);
      })
      .then(function () {
        // Redirects when the toast closes, so the timer and the button share
        // one path.
        toast.success({
          title: t('heimdal_app.PassRecovery.revoke_mfa_msg', {
            phoneNumber: obfuscatedPhoneNumber,
          }),
          action: (
            <Button size="sm" variant="secondary">
              {t('heimdal_app.PassRecovery.sign_in_now')}
            </Button>
          ),
          actionAltText: t('heimdal_app.PassRecovery.sign_in_now'),
          onDismiss: () => window.location.replace('/'),
        });
        setRevoked(true);
      })
      .catch(function (error) {
        console.error('Recover.revokeMFA:', getSafeErrorIdentifier(error));
        // An expired, invalid or already-spent action code is only fixable by
        // requesting a new link; anything else is a generic retry.
        if (
          error.code === 'auth/expired-action-code' ||
          error.code === 'auth/invalid-action-code'
        ) {
          showError('heimdal_app.PassRecovery.token_expired');
        } else {
          showError('heimdal_app.Login.try_again_later');
        }
      })
      // Success keeps the button dead via `revoked`; a failure has to hand it
      // back so the user can retry with a fresh link.
      .finally(() => {
        setRevoking(false);
      });
  };

  const handlePassword = event => {
    event.preventDefault();
    const auth = getAuth();
    auth.tenantId = tenant;

    if (password === passwordConfirmation) {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const googleOobKey = urlParams.get('oobCode');

        verifyPasswordResetCode(auth, googleOobKey)
          .then(info => {
            props.nistCheckPassword({
              info,
              password,
            });
          })
          .catch(() => {
            showError('heimdal_app.PassRecovery.token_expired');
          });
      } catch (error) {
        console.error('Recover.handlePassword:', getSafeErrorIdentifier(error));
      }
    } else {
      showError('heimdal_app.PassRecovery.password_match');
    }
  };

  const handleSubmit = event => {
    event.preventDefault();

    // One value for both the check and the provider, so the address that passed
    // validation is the address Firebase receives.
    const normalizedEmail = email.trim();

    // Format leaks nothing about the account, so this keeps the neutral answer
    // below intact. Without it a malformed address gets "check your email".
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      showError('heimdal_app.PassRecovery.invalid_email');
      return;
    }

    const auth = getAuth();
    auth.tenantId = tenant;
    // Picks the language of the mail Firebase sends.
    auth.languageCode = language;

    // Asking the provider who owns the address is what Email Enumeration
    // Protection blocks, so the request goes out and the answer stays neutral.
    sendPasswordResetEmail(auth, normalizedEmail)
      .then(() => {
        showSuccess('heimdal_app.PassRecovery.check_email');
      })
      .catch(error => {
        console.error('Recover.handleSubmit:', getSafeErrorIdentifier(error));
        if (error.code === 'auth/user-not-found') {
          // Whether the address has a password account is the provider's
          // secret, so the unregistered case answers like the registered one.
          showSuccess('heimdal_app.PassRecovery.check_email');
        } else if (error.code === 'auth/invalid-email') {
          showError('heimdal_app.PassRecovery.invalid_email');
        } else {
          showError('heimdal_app.Login.try_again_later');
        }
      });
  };

  // Eye toggle matching LoginForm: a bare button hosting the visibility icon,
  // rendered inside the TextField endAdornment slot.
  const passwordToggle = (onClick, onMouseDown, visible) => (
    <button
      type="button"
      aria-label="toggle password visibility"
      onClick={onClick}
      onMouseDown={onMouseDown}
      className="flex cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-1 hover:bg-accent-tint">
      <IconComponent
        iconName={visible ? 'eye-off-outline' : 'eye-outline'}
        className={'icon'}
        style={{ color: 'var(--zippedi-text-primary)' }}
      />
    </button>
  );

  return (
    <LoginLayout helpUrl={fullWhatsAppUrl}>
      <div className="flex flex-col">
        {mode === 'resetPassword' ? (
          <>
            <AppText variant="titleXLarge" as="h3" className="mb-2">
              {t('heimdal_app.PassRecovery.new_password_description')}
            </AppText>
            <InputField
              id="recover-new-password"
              className="mb-3.5"
              label={t('heimdal_app.PassRecovery.new_password')}
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={password}
              autoFocus
              onChange={handleChangePassword}
              endAdornment={passwordToggle(
                handleClickShowPassword,
                handleMouseDownPassword,
                showPassword,
              )}
            />
            <InputField
              id="recover-confirm-password"
              className="mb-3.5"
              label={t('heimdal_app.PassRecovery.confirm_password')}
              type={showPasswordConfirmation ? 'text' : 'password'}
              name="password"
              value={passwordConfirmation}
              onChange={handleChangePasswordConfirmation}
              endAdornment={passwordToggle(
                handleClickShowPasswordConfirmation,
                handleMouseDownPasswordConfirmation,
                showPasswordConfirmation,
              )}
            />
            <Button variant="primary" fullWidth onClick={handlePassword}>
              {t('heimdal_app.PassRecovery.confirm')}
            </Button>
          </>
        ) : mode === 'revertSecondFactorAddition' ? (
          <>
            <AppText variant="titleXLarge" as="h3" className="mb-2">
              {t('heimdal_app.PassRecovery.disable_2FA_description')}
            </AppText>
            {/* One-shot code: `revoking` closes the in-flight window, `revoked`
                keeps it closed once the notice is up. */}
            <Button
              variant="primary"
              fullWidth
              disabled={revoking || revoked}
              onClick={revokeMFA}>
              {t('heimdal_app.PassRecovery.disable_2FA')}
            </Button>
          </>
        ) : (
          <>
            <BackLink
              onClick={() => navigate(-1)}
              label={t('heimdal_app.PassRecovery.go_back')}
            />
            <AppText variant="titleXLarge" as="h3" className="mb-2">
              {t('heimdal_app.PassRecovery.email_title')}
            </AppText>
            <AppText variant="bodyMedium" as="p" className="mb-6 text-secondary">
              {t('heimdal_app.PassRecovery.email_description')}
            </AppText>
            <InputField
              id="recover-email"
              className="mb-3.5"
              label={t('heimdal_app.Login.email')}
              value={email}
              type="email"
              name="email"
              autoFocus
              onChange={handleChangeEmail}
            />
            <Button variant="primary" fullWidth onClick={handleSubmit}>
              {t('heimdal_app.PassRecovery.confirm')}
            </Button>
          </>
        )}
      </div>
    </LoginLayout>
  );
}
